import React from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiCheckCircle, FiShield, FiClock, FiMail, FiHome } = FiIcons;

const PortalFooter = ({ hideBranding }) => {
  if (hideBranding) return null;
  return (
    <footer className="py-8 text-center opacity-40 hover:opacity-100 transition-opacity mt-8 no-print">
      <div className="flex items-center justify-center gap-1.5 text-[9px] font-bold text-gray-400 uppercase tracking-[0.2em]">
        <SafeIcon icon={FiShield} size={10} />
        <span>Sikker varslingsportal levert av</span>
        <a href="https://hmsvarsling.no" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 transition-colors underline underline-offset-2">
          HMSVarsling.no
        </a>
      </div>
    </footer>
  );
};

const WhistleblowerConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const reportId = location.state?.reportId;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white py-12 flex flex-col">
      <div className="container mx-auto px-4 flex-1">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto text-center" >
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring" }} className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6" >
            <SafeIcon icon={FiCheckCircle} className="text-white text-4xl" />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="bg-white rounded-xl shadow-lg p-8 mb-8" >
            <h1 className="text-3xl font-bold text-gray-900 mb-4"> Varsel mottatt! </h1>
            <p className="text-lg text-gray-600 mb-6"> Takk for at du sendte inn varselet. Det er nå registrert i systemet og vil bli behandlet i henhold til gjeldende prosedyrer. </p>
            {reportId && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-blue-900 mb-2">Referansenummer:</h3>
                <p className="text-2xl font-mono text-blue-800">{reportId}</p>
                <p className="text-sm text-blue-700 mt-2"> Ta vare på dette nummeret for fremtidig referanse </p>
              </div>
            )}
            <div className="text-left">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center"> Hva skjer nå? </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1"> <span className="text-blue-600 font-semibold text-sm">1</span> </div>
                  <div> <h4 className="font-medium text-gray-900">Automatisk registrering</h4> <p className="text-gray-600 text-sm">Varselet er automatisk registrert og tildelt en saksbehandler</p> </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1"> <span className="text-blue-600 font-semibold text-sm">2</span> </div>
                  <div> <h4 className="font-medium text-gray-900">Innledende vurdering</h4> <p className="text-gray-600 text-sm">Bedriften vil gjennomføre en innledende vurdering innen 3 arbeidsdager</p> </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1"> <span className="text-blue-600 font-semibold text-sm">3</span> </div>
                  <div> <h4 className="font-medium text-gray-900">Oppfølging</h4> <p className="text-gray-600 text-sm">En strukturert handlingsplan vil bli fulgt for å undersøke saken grundig</p> </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="bg-white rounded-xl shadow-lg p-6 mb-8" >
            <div className="flex items-start space-x-3">
              <SafeIcon icon={FiShield} className="text-blue-600 text-xl mt-1" />
              <div className="text-left">
                <h3 className="font-semibold text-gray-900 mb-2">Dine rettigheter</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Du er beskyttet mot gjengjeldelse</li> <li>• Din identitet holdes konfidensiell</li> <li>• Du har rett til informasjon om sakens fremgang</li> <li>• Du kan kontakte oss hvis du har spørsmål</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0 }} className="space-y-4" >
            <button onClick={() => navigate('/')} className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2" >
              <SafeIcon icon={FiHome} /> <span>Tilbake til forsiden</span>
            </button>
          </motion.div>
        </motion.div>
      </div>
      <PortalFooter />
    </div>
  );
};

export default WhistleblowerConfirmation;